import { Injectable } from '@angular/core';
import { Router, NavigationExtras, ActivatedRoute } from "@angular/router";
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, forkJoin } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';


import { AccountEndpoint } from '../../endpoints/account-endpoint.service';
import { AuthService } from '../auth/auth.service';
import { ManageUser, User, UserRoles, UserTenantMap } from '@app/shared/models/Authentication/user.model';
import { Role } from '@app/shared/models/Authentication/role.model';
import { Permission, PermissionNames, PermissionValues, ServicePermissionValues } from '@app/shared/models/Authentication/permission.model';
import { UserEdit } from '@app/shared/models/Authentication/user-edit.model';
import { ApiResponse } from '@app/shared/models/Common/apiresponse.model';
import CustomStore from 'devextreme/data/custom_store';
import { AppService } from '@app/shared/models/Common/enums';
import { UserState } from '@app/shared/models/dto/user.state.model';


export type RolesChangedOperation = "add" | 'delete' | "modify";
export type RolesChangedEventArg = { roles: Role[] | string[], operation: RolesChangedOperation };



@Injectable()
export class AccountService {


  public static readonly roleAddedOperation: RolesChangedOperation = "add";
  public static readonly roleDeletedOperation: RolesChangedOperation = "delete";
  public static readonly roleModifiedOperation: RolesChangedOperation = "modify";

  private _rolesChanged = new Subject<RolesChangedEventArg>();

  private sourceUserState = new BehaviorSubject<UserState>(new UserState());
  userState = this.sourceUserState.asObservable();

  constructor(private router: Router, private http: HttpClient, private authService: AuthService,
    private route: ActivatedRoute,
    private accountEndpoint: AccountEndpoint) {

  }
  setUserState(userState: UserState): void {
    this.sourceUserState.next(userState);
  }
  getUserState(): UserState {
    return this.sourceUserState.value;
  }

  gotoUserList() {
    this.router.navigate([`/admin/user`], { relativeTo: this.route });
  }
  gotoSelectedUser() {
    this.router.navigate([`/admin/manageuser`], { relativeTo: this.route });
  }

  getUser(userId?: string) {
    return this.accountEndpoint.getUserEndpoint<User>(userId);
  }

  getManageUser(userId?: string) {
    return this.accountEndpoint.getManageUserEndpoint<ManageUser>(userId);
  }
 getHealthManageUserProperties(userId?: string) {
    return this.accountEndpoint.getHealthManageUserProperties<any>(userId);
  }
getLifeManageUserProperties(userId?: string) {
    return this.accountEndpoint.getLifeManageUserProperties<any>(userId);
  }

  getUserAndRoles(userId?: string) {

    return forkJoin(
      this.accountEndpoint.getUserEndpoint<User>(userId),
      this.accountEndpoint.getRolesEndpoint<Role[]>());
  }

  getUsers(page?: number, pageSize?: number, tenantId?: number) {

    return this.accountEndpoint.getUsersEndpoint<User[]>(page, pageSize, tenantId);
  }
  getAllUsersList(): CustomStore {
    return this.accountEndpoint.getAllUsersListEndpoint();
  }
   getAllUsers(searchTxt: string): CustomStore  {
    return this.accountEndpoint.getAllUsersEndpoint(searchTxt);
  }
  getUsersByTenant(tenantId: number, businessEntityId: number, serviceId: number) {

    return this.accountEndpoint.getUsersByTenantEndpoint<any[]>(tenantId, businessEntityId, serviceId);
  }
  getReviewerList() {
    return this.accountEndpoint.getReviewerList<any[]>();
  }
  getUsersByTenantId(tenantId: number) {

    return this.accountEndpoint.getUsersByTenantIdEndpoint<any[]>(tenantId);
  }
  getUsersAndRoles(page?: number, pageSize?: number) {

    return forkJoin(
      this.accountEndpoint.getUsersEndpoint<User[]>(page, pageSize),
      this.accountEndpoint.getRolesEndpoint<Role[]>());
  }
  sendWelcomeEmail(userId: string) {
    return this.accountEndpoint.sendWelcomeEmailEndPoint<ApiResponse>(userId);
  }

  updateUser(user: UserEdit) {
    if (user.id) {
      return this.accountEndpoint.getUpdateUserEndpoint<ApiResponse>(user, user.id);
    } else {
      return this.accountEndpoint.getUserByUserNameEndpoint<User>(user.userName).pipe(
        mergeMap(foundUser => {
          user.id = foundUser.id;
          return this.accountEndpoint.getUpdateUserEndpoint<ApiResponse>(user, user.id);
        }));
    }
  }


  newUser(user: any) {
    return this.accountEndpoint.getNewUserEndpoint<any>(user);
  }
  upsertuser(obj: any): Observable<any> {
    return this.accountEndpoint.upsertuserEndpoint<any>(obj);
  }
  updateUserProperties(obj: any): Observable<any> {
    return this.accountEndpoint.updateUserProperties<any>(obj);
  }
   updateUserRoles(obj: any): Observable<any> {
    return this.accountEndpoint.updateUserRoles<any>(obj);
  }
   updateTenantService(obj: any): Observable<any> {
    return this.accountEndpoint.updateTenantService<any>(obj);
  }
  upsert_userproperties(obj: any): Observable<any> {
    return this.accountEndpoint.upsertuserpropertiesEndpoint<any>(obj);
  }
  updatePassword(obj: any): Observable<any> {
    return this.accountEndpoint.updatePasswordEndpoint<any>(obj);
  }

  getUserPreferences() {
    return this.accountEndpoint.getUserPreferencesEndpoint<string>();
  }

  updateUserPreferences(configuration: string) {
    return this.accountEndpoint.getUpdateUserPreferencesEndpoint(configuration);
  }


  deleteUser(userOrUserId: string | UserEdit): Observable<User> {

    if (typeof userOrUserId === 'string' || userOrUserId instanceof String) {
      return this.accountEndpoint.getDeleteUserEndpoint<User>(<string>userOrUserId).pipe<User>(
        tap(data => this.onRolesUserCountChanged(data.roles)));
    }
    else {

      if (userOrUserId.id) {
        return this.deleteUser(userOrUserId.id);
      }
      else {
        return this.accountEndpoint.getUserByUserNameEndpoint<User>(userOrUserId.userName).pipe<User>(
          mergeMap(user => this.deleteUser(user.id)));
      }
    }
  }


  unblockUser(userId: string) {
    return this.accountEndpoint.getUnblockUserEndpoint(userId);
  }


  userHasPermission(permissionValue: PermissionValues, serviceId: number = 0): boolean {
    let serviceStr: string;
    if (serviceId != 0) {
      serviceStr = AppService[serviceId];
      return this.permissions.some(p => p == serviceStr.toLowerCase() + '.' + permissionValue);
    }
    else {
      return this.permissions.some(p => p == permissionValue);
    }
  }
  refreshLoggedInUser() {
    return this.authService.refreshLogin();
  }




  getRoles(page?: number, pageSize?: number) {

    return this.accountEndpoint.getRolesEndpoint<Role[]>(page, pageSize);
  }

  getRolesByTenant(tenantId: number) {
    return this.accountEndpoint.getRolesByTenantEndpoint<Role[]>(tenantId);

  }

  getRolesAndPermissions(page?: number, pageSize?: number) {

    return forkJoin(
      this.accountEndpoint.getRolesEndpoint<Role[]>(page, pageSize),
      this.accountEndpoint.getPermissionsEndpoint<Permission[]>());
  }


  updateRole(role: Role) {
    if (role.id) {
      return this.accountEndpoint.getUpdateRoleEndpoint(role, role.id).pipe(
        tap(data => this.onRolesChanged([role], AccountService.roleModifiedOperation)));
    }
    else {
      // return this.accountEndpoint.getRoleByRoleNameEndpoint<Role>(role.name).pipe<Role>(
      //   mergeMap(foundRole => {
      //     role.id = foundRole.id;
      //     return this.accountEndpoint.getUpdateRoleEndpoint(role, role.id)
      //   }),
      //   tap(data => this.onRolesChanged([role], AccountService.roleModifiedOperation)));
    }
  }


  newRole(role: Role) {
    return this.accountEndpoint.getNewRoleEndpoint<Role>(role).pipe<Role>(
      tap(data => this.onRolesChanged([role], AccountService.roleAddedOperation)));
  }


  deleteRole(roleOrRoleId: string | Role): Observable<Role> {

    if (typeof roleOrRoleId === 'string' || roleOrRoleId instanceof String) {
      return this.accountEndpoint.getDeleteRoleEndpoint<Role>(<string>roleOrRoleId).pipe<Role>(
        tap(data => this.onRolesChanged([data], AccountService.roleDeletedOperation)));
    }
    else {

      if (roleOrRoleId.id) {
        return this.deleteRole(roleOrRoleId.id);
      }
      else {
        return this.accountEndpoint.getRoleByRoleNameEndpoint<Role>(roleOrRoleId.name).pipe<Role>(
          mergeMap(role => this.deleteRole(role.id)));
      }
    }
  }

  getPermissions() {

    return this.accountEndpoint.getPermissionsEndpoint<Permission[]>();
  }


  private onRolesChanged(roles: Role[] | string[], op: RolesChangedOperation) {
    this._rolesChanged.next({ roles: roles, operation: op });
  }


  onRolesUserCountChanged(roles: Role[] | string[]) {
    return this.onRolesChanged(roles, AccountService.roleModifiedOperation);
  }


  getRolesChangedEvent(): Observable<RolesChangedEventArg> {
    return this._rolesChanged.asObservable();
  }


  get permissions(): ServicePermissionValues[] {
    return this.authService.userPermissions;
  }
  get userRoles(): UserRoles[] {
    return this.authService.userRoles;
  }
  get hasIndProductAccess(): boolean {
    return this.authService.hasIndProductAccess;
  }

  get currentUser() {
    return this.authService.currentUser;
  }
  get isReinsurer() {
    return this.authService.currentUser.entityType == 'REINSURER';
  }
  get isInsurer() {
    return this.authService.currentUser.entityType == 'INSURER';
  }
  get isBroker() {
    return this.authService.currentUser.entityType == 'BROKER';
  }
  get getUserTenants(): UserTenantMap[] {
    return this.authService.userTenantMap;
  }
  get checkUserConsent(): Observable<Boolean> {
    return this.accountEndpoint.checkUserConsentEndpoint();
  }
  get isSalesUser() {
    return this.authService.currentUser.entityType == "BROKER" || this.authService.currentUser.roles.indexOf("Insurer Sales") > -1;
  }
  amendUserConsent(): Observable<any> {
    return this.accountEndpoint.amendUserConsent<any>();
  }
  switchUserTenant(tenantId: number): Observable<any> {
    return this.accountEndpoint.switchUserTenant(tenantId, this.authService.currentUser.id);
  }
  getUserTenantMap(userId: string): Observable<any> {
    return this.accountEndpoint.getUserTenantMapEndpoint(userId);
  }
  checkIndQuoteAccess(): Observable<any> {
    return this.accountEndpoint.checkIndQuoteAccess();
  }
  importUsers(): Observable<any> {
    return this.accountEndpoint.importUsers()
  }
  resetBrokerPassword() {
    return this.accountEndpoint.resetBrokerPassword<ApiResponse>()
  }
  createLogoutContext(): Observable<any> {
    return this.accountEndpoint.createLogoutContext();
  }

  uploadTempBrokerUsers(file: File): Observable<any> {
    return this.accountEndpoint.uploadTempBrokerUsers<any>(file);
  }
  insertTempBrokerUsers(payload: any): Observable<any> {
    return this.accountEndpoint.insertTempBrokerUsers<any>(payload);
  }
  getPendingBrokerUsers(): Observable<any> {
    return this.accountEndpoint.getPendingBrokerUsers<any>();
  }
  deleteTempBrokerUsers(ids: number[]): Observable<any> {
    return this.accountEndpoint.deleteTempBrokerUsers<any>(ids);
  }

  getTempBrokerUsers(): Observable<any> {
    return this.accountEndpoint.getTempBrokerUsersEndpoint<any>();
  }

  updateTempBrokerUser(row: any): Observable<any> {
    return this.accountEndpoint.updateTempBrokerUserEndpoint<any>(row);
  }
}
